import { ChangeDetectionStrategy, Component, Input, OnInit, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { viewportSizeDetector } from '~app/modules/generic/helpers';
import { SeoContentBanner } from '~shared/models/seo-content-banner.model';
import { environment } from '~env/environment';
import { isPlatformBrowser } from '@angular/common';
import { NUMBER_968 } from '~shared/constants';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { BannerSponsoring } from '~app/modules/storyblok/models';

@Component({
  selector: 'app-image-header',
  templateUrl: './image-header.component.html',
  styleUrls: ['./image-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageHeaderComponent implements OnInit {
  @Input() bannerId: string;
  @Input() promoImage: any;
  @Input() bannerLink?: string;
  @Input() title: string;
  @Input() isExternalLink = false;
  @Input() trackingName: string;
  @Input() sponsorItem?: BannerSponsoring;
  viewportSize = null;
  marketplaceLink = environment.appBaseUrl();
  public banner$ = new BehaviorSubject<any>(undefined);

  constructor(private gtmDatalayerService: GtmDatalayerService, @Inject(PLATFORM_ID) private platformId: Object) {}

  /**
   * Directive which listens to window resize event
   * @param event
   */
  @HostListener('window:resize', ['$event'])
  windowResizeListener(event?) {
    const viewportSize = viewportSizeDetector(window.innerWidth, true);
    if (this.viewportSize !== viewportSize) {
      this.viewportSize = viewportSize;
      this.generatePlpBanner();
    }
  }

  get listOfImages() {
    return this.promoImage.reduce((accumulator: SeoContentBanner[], value: SeoContentBanner) => {
      return { ...accumulator, [value.viewport]: value.url };
    }, {});
  }

  ngOnInit(): void {
    this.viewportSize = viewportSizeDetector(window.innerWidth, true);
    if (Object.keys(this.promoImage).length > 0) {
      this.generatePlpBanner();
    }
  }

  private loadGenericGaSchema(): { [key: string]: string | object } {
    return {
      ecommerce: {
        creative_name: 'Banner',
        creative_slot: this.trackingName || '',
        promotion_name: this.title || '',
        promotion_id: this.bannerId || '',
        ...(this.sponsorItem?.isSponsored && {
          items: [
            {
              item_id: this.sponsorItem._uid,
              item_name: this.sponsorItem.advertiserPayerName,
              item_seller: this.sponsorItem.advertiserName,
            },
          ],
        }),
      },
    };
  }

  generatePlpBanner(): void {
    if (isPlatformBrowser(this.platformId)) {
      const banner = this.listOfImages[this.viewportSize] || this.listOfImages[NUMBER_968];
      const img = new Image();
      img.src = banner;
      if (banner) {
        img.onload = () => this.banner$.next(banner);
        return;
      }
      this.banner$.next(banner);
    }
  }

  fireSelectPromoGAEvent(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SelectPromotion,
      ...this.loadGenericGaSchema(),
    });
  }

  fireViewPromoGAEvent(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.ViewPromotion,
      ...this.loadGenericGaSchema(),
    });
  }

  navigateTo(): void {
    if (this.bannerLink) {
      this.isExternalLink
        ? window.open(this.bannerLink, '_blank')
        : (window.location.href = this.marketplaceLink + this.bannerLink);
    }
  }
}
